import {
  Box,
  type BoxProps,
  Button,
  Flex,
  GridItem,
  Link,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
} from '@chakra-ui/react'
import { cx } from '@chakra-ui/utils'
import NextLink from 'next/link'
import { useRouter } from 'next/router'

import { useNavigation } from '../../../context/NavigationProvider'
import { type IReducedAsset } from '../../../types/reducedTypes'
import { pxToRem } from '../../../utils/pxToRem'
import ContainerGrid from '../../grid/ContainerGrid/ContainerGrid'
import ContentfulImage from '../../shared-components/ContentfulImage/ContentfulImage'
import { type EntryId } from '../../shared-components/DocumentToReactRender/DocumentToReactRender'

interface NavItem {
  id: EntryId
  text: string
}

interface NavItemGroup {
  id: EntryId
  title: string
  links: NavItem[]
}

interface MainNavItem extends NavItem {
  groups: NavItemGroup[]
}

export interface MainNavProps extends BoxProps {
  logo?: IReducedAsset
  logoUrl: string
  navItems: MainNavItem[]
  isBusinessFinance?: boolean
}

export default function MainNav({
  className,
  logoUrl,
  logo,
  navItems,
  isBusinessFinance = false,
  ...boxProps
}: MainNavProps) {
  const { findFullUrlFromId } = useNavigation()

  const router = useRouter()
  const currentPath = router.asPath

  return (
    <Box
      className={cx('main-nav', className)}
      bg="white"
      boxShadow="0px 0px 16px rgba(0, 31, 38, 0.345497)"
      {...boxProps}
    >
      <ContainerGrid>
        <GridItem
          colSpan={isBusinessFinance ? 3 : 2}
          display="flex"
          alignItems="center"
          py="15px"
        >
          <Link as={NextLink} href={logoUrl} h="31px" py="5px">
            {logo ? (
              <ContentfulImage
                image={logo}
                overrideValues={{
                  width: 138,
                  height: 21,
                }}
                style={{ objectFit: 'contain', width: 'auto', height: '100%' }}
              />
            ) : (
              <></>
            )}
          </Link>
        </GridItem>

        <GridItem colSpan={isBusinessFinance ? 9 : 10}>
          {navItems.length > 0 && (
            <Flex w="100%" minH="60px" align="stretch" gap="12px" as={List}>
              {navItems.map(navItem => {
                const path = findFullUrlFromId(navItem.id) ?? ''
                const isSelected = currentPath === `/${path}`

                // Fallback: if navItem.groups is empty, use global submenuLinks if available
                const submenuLinks =
                  navItem.groups && navItem.groups.length
                    ? navItem.groups
                    : typeof window === 'undefined' &&
                        (boxProps as any).submenuLinks
                      ? [
                          {
                            title: 'Links',
                            links: (boxProps as any).submenuLinks,
                          },
                        ]
                      : []

                return (
                  <ListItem key={navItem.id}>
                    <Menu isLazy variant="navStyles">
                      {({ isOpen }) => (
                        <>
                          {!navItem.groups.length ? (
                            <Link
                              href={`/${path}`}
                              as={NextLink}
                              fontSize={pxToRem(18)}
                              textAlign="center"
                              fontWeight={isSelected || isOpen ? '400' : '300'}
                              color="primary.darkGreen.100"
                              _groupHover={{ color: 'primary.teal.40' }}
                              _focusVisible={{
                                boxShadow: 'inset 0 0 0 4px #00d1b6',
                                outlineColor: 'primary.darkGreen.100',
                                outlineOffset: '-4px',
                                textDecoration: 'underline',
                              }}
                              display="flex"
                              alignItems="center"
                              wordBreak="initial"
                              padding={`${pxToRem(8)} 4px`}
                              lineHeight="1.5rem"
                              height="100%"
                            >
                              {navItem.text}
                            </Link>
                          ) : (
                            <MenuButton
                              as={Button}
                              variant="none"
                              cursor="pointer"
                              role="group"
                              px="0"
                              fontSize={pxToRem(18)}
                              textAlign="center"
                              fontWeight={
                                isSelected ? '400' : isOpen ? '300' : '100'
                              }
                              color="primary.darkGreen.100"
                              _groupHover={{ color: 'primary.teal.40' }}
                              position="relative"
                              alignItems="center"
                              height="100%"
                              whiteSpace="normal"
                              wordBreak="initial"
                              padding={`${pxToRem(8)} 4px`}
                              lineHeight="1.5rem"
                              _hover={{ textDecoration: 'underline' }}
                              _focusVisible={{
                                boxShadow: 'inset 0 0 0 4px #00A792',
                                outlineColor: 'primary.darkGreen.100',
                                outlineOffset: '-4px',
                                borderRadius: 0,
                              }}
                            >
                              {navItem.text}

                              <Box
                                roundedTop="2.5px"
                                h="4px"
                                bg="primary.darkGreen.100"
                                position="absolute"
                                bottom="0"
                                right="0"
                                left="0"
                                opacity={isOpen ? 1 : 0}
                                transition="0.3s"
                              />
                            </MenuButton>
                          )}

                          <MenuList
                            transform="none !important" // Important to override any inline styles
                          >
                            <Box w="100vw">
                              <ContainerGrid>
                                <GridItem colStart={3} colSpan={10}>
                                  <Flex
                                    direction="row"
                                    w="100%"
                                    justifyContent="center"
                                    py="40px"
                                    color="primary.white.100"
                                    gap="32px"
                                    flexWrap="wrap"
                                    display="flex"
                                  >
                                    {submenuLinks.map((group, index) => (
                                      <Flex
                                        key={`${group.title}-${index}`}
                                        gap="32px"
                                        flex="1 0 0"
                                        minW="30%"
                                      >
                                        <MenuGroup title={group.title}>
                                          {group.links?.map(
                                            (
                                              link: {
                                                id: string
                                                text: string
                                              },
                                              index: number,
                                            ) => {
                                              const fullPath = findFullUrlFromId
                                                ? findFullUrlFromId(link.id)
                                                : link.id
                                              if (!fullPath) {
                                                console.warn(
                                                  'fullPath is undefined for',
                                                  link,
                                                )
                                                return null
                                              }
                                              return (
                                                <MenuItem
                                                  key={`${link.id}-${index}`}
                                                  href={fullPath ?? ''}
                                                  as={NextLink}
                                                >
                                                  {link.text}
                                                </MenuItem>
                                              )
                                            },
                                          )}
                                        </MenuGroup>
                                      </Flex>
                                    ))}
                                  </Flex>
                                </GridItem>
                              </ContainerGrid>
                            </Box>
                          </MenuList>
                        </>
                      )}
                    </Menu>
                  </ListItem>
                )
              })}
            </Flex>
          )}
        </GridItem>
      </ContainerGrid>
    </Box>
  )
}
